import React from 'react';
import {
  Box, Card, Typography, Divider, Table, TableHead, TableBody, TableRow, TableCell,
  Button, Chip, Stack, CircularProgress, Link,
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PublicDocumentPayForm from './PublicDocumentPayForm';
import SquareInvoicePayForm from '../payments/SquareInvoicePayForm';

/**
 * Shared, read-only renderer for a normalized quote/invoice document
 * (produced by the backend documentService). Used by the public hosted pages.
 */

const money = (n) =>
  n === null || n === undefined || n === '' ? '' : `$${Number(n).toFixed(2)}`;

const formatDate = (d) => {
  if (!d) return '—';
  const date = new Date(d);
  if (isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
};

const STATUS_COLORS = {
  PENDING: 'warning', SENT: 'info', ACTIVE: 'info', ACCEPTED: 'success',
  REJECTED: 'error', EXPIRED: 'default', DUE: 'warning', PAID: 'success', REFUNDED: 'default',
};

const priceFont = { fontFamily: 'monospace', fontVariantNumeric: 'tabular-nums' };

const DocumentView = ({ document, onAccept, accepting = false, acceptError = null, payToken = null, onPaid = null }) => {
  if (!document) return null;

  const isQuote = document.type === 'QUOTE';
  const accent = document.company?.primaryColor || '#1976d2';
  const company = document.company || {};
  const customer = document.customer || {};
  const addr = company.address || {};
  const companyAddress = [addr.street, addr.street_line_2, [addr.city, addr.state, addr.zip].filter(Boolean).join(', ')]
    .filter(Boolean);

  const canAccept = isQuote && ['PENDING', 'SENT', 'ACTIVE'].includes(document.status);
  const accepted = isQuote && document.status === 'ACCEPTED';

  // Online payment: unpaid invoices, and accepted quotes (accept → pay → booked)
  const canPay = Boolean(payToken && onPaid) &&
    ((!isQuote && document.status === 'DUE') || (isQuote && accepted));

  return (
    <Box sx={{ bgcolor: '#f5f5f5', minHeight: '100vh', py: { xs: 2, sm: 5 }, px: 2,
      '@media print': { bgcolor: '#fff', py: 0 } }}>
      <Card elevation={3} sx={{ maxWidth: 800, mx: 'auto', p: { xs: 3, sm: 5 }, borderRadius: 3,
        '@media print': { boxShadow: 'none' } }}>

        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2}>
          <Box>
            {company.logoUrl
              ? <Box component="img" src={company.logoUrl} alt={company.name}
                  sx={{ maxHeight: 64, mb: 1 }} />
              : <Typography variant="h5" fontWeight={800} sx={{ color: accent }}>{company.name}</Typography>}
            {company.logoUrl && (
              <Typography variant="subtitle1" fontWeight={700}>{company.name}</Typography>
            )}
            {companyAddress.map((line, i) => (
              <Typography key={i} variant="body2" color="text.secondary">{line}</Typography>
            ))}
            {company.phone && <Typography variant="body2" color="text.secondary">{company.phone}</Typography>}
            {company.email && <Typography variant="body2" color="text.secondary">{company.email}</Typography>}
          </Box>
          <Box textAlign="right">
            <Typography variant="h4" fontWeight={800} color="text.secondary" letterSpacing={1}>
              {isQuote ? 'QUOTE' : 'INVOICE'}
            </Typography>
            <Typography variant="body2"><strong>{document.number}</strong></Typography>
            <Chip size="small" label={document.status}
              color={STATUS_COLORS[document.status] || 'default'} sx={{ mt: 0.5 }} />
          </Box>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Meta + Bill To */}
        <Box display="flex" justifyContent="space-between" flexWrap="wrap" gap={3}>
          <Box>
            <Typography variant="overline" color="text.secondary">Bill To</Typography>
            <Typography variant="body1" fontWeight={600}>{customer.name || '—'}</Typography>
            {customer.address && <Typography variant="body2" color="text.secondary">{customer.address}</Typography>}
            {customer.email && <Typography variant="body2" color="text.secondary">{customer.email}</Typography>}
            {customer.phone && <Typography variant="body2" color="text.secondary">{customer.phone}</Typography>}
          </Box>
          <Box textAlign="right">
            <Typography variant="body2" color="text.secondary">
              {isQuote ? 'Date' : 'Issued'}: <strong>{formatDate(document.issueDate)}</strong>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {isQuote ? 'Valid until' : 'Due'}: <strong>{formatDate(isQuote ? document.validUntil : document.dueDate)}</strong>
            </Typography>
            {document.scheduledDate && (
              <Typography variant="body2" color="text.secondary">
                Service: <strong>{formatDate(document.scheduledDate)}{document.scheduledTime ? ` (${document.scheduledTime})` : ''}</strong>
              </Typography>
            )}
            {document.frequency && (
              <Typography variant="body2" color="text.secondary">Frequency: <strong>{document.frequency}</strong></Typography>
            )}
          </Box>
        </Box>

        <Box sx={{ mt: 3 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Description</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700 }}>Qty</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Amount</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(document.lineItems || []).map((item, i) => (
                <TableRow key={i}>
                  <TableCell>{item.description}</TableCell>
                  <TableCell align="center">{item.quantity ?? 1}</TableCell>
                  <TableCell align="right" sx={priceFont}>
                    {item.amount === null || item.amount === undefined
                      ? <Typography variant="caption" color="text.secondary">Included</Typography>
                      : money(item.amount)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>

        {/* Totals */}
        <Box display="flex" justifyContent="flex-end" sx={{ mt: 2 }}>
          <Box sx={{ width: { xs: '100%', sm: 320 } }}>
            <Row label="Subtotal" value={money(document.subtotal)} />
            {document.taxEnabled && (
              <Row label={`${document.taxLabel} (${(document.taxRate * 100).toFixed(document.taxRate * 100 % 1 ? 2 : 0)}%)`}
                value={money(document.taxAmount)} />
            )}
            <Divider sx={{ my: 1 }} />
            <Row label="Total" value={money(document.total)} bold accent={accent} />
            {!isQuote && (
              <>
                <Row label="Amount Paid" value={money(document.amountPaid)} />
                <Row label="Amount Due" value={money(document.amountDue)} bold
                  accent={document.amountDue > 0 ? accent : 'green'} />
              </>
            )}
          </Box>
        </Box>

        {document.service && (document.service.description || document.service.url || document.service.checklistUrl) && (
          <Box sx={{ mt: 3, pl: 2, borderLeft: `4px solid ${accent}` }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{document.service.name}</Typography>
            {document.service.description && (
              <Typography variant="body2" color="text.secondary">{document.service.description}</Typography>
            )}
            {(document.service.url || document.service.checklistUrl) && (
              <Stack direction="row" spacing={2} sx={{ mt: 0.5, '@media print': { display: 'none' } }}>
                {document.service.url && (
                  <Link href={document.service.url} variant="body2" sx={{ color: accent }}>About this service</Link>
                )}
                {document.service.checklistUrl && (
                  <Link href={document.service.checklistUrl} variant="body2" sx={{ color: accent }}>Cleaning checklist</Link>
                )}
              </Stack>
            )}
          </Box>
        )}

        {document.specialInstructions && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="overline" color="text.secondary">Notes</Typography>
            <Typography variant="body2">{document.specialInstructions}</Typography>
          </Box>
        )}

        <Divider sx={{ my: 3 }} />

        {/* Actions (hidden when printing) */}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="flex-end"
          sx={{ '@media print': { display: 'none' } }}>
          <Button variant="outlined" startIcon={<PrintIcon />} onClick={() => window.print()}>
            Print / Save PDF
          </Button>
          {accepted && (
            <Button variant="contained" color="success" startIcon={<CheckCircleIcon />} disabled>
              Accepted
            </Button>
          )}
          {canAccept && onAccept && (
            <Button variant="contained" onClick={onAccept} disabled={accepting}
              sx={{ bgcolor: accent }} startIcon={accepting ? <CircularProgress size={18} color="inherit" /> : <CheckCircleIcon />}>
              {accepting ? 'Accepting…' : 'Accept Quote'}
            </Button>
          )}
        </Stack>
        {acceptError && (
          <Typography color="error" variant="body2" sx={{ mt: 1, textAlign: 'right' }}>{acceptError}</Typography>
        )}

        {/* Online payment (hidden when printing) */}
        {canPay && (
          <Box sx={{ mt: 3, '@media print': { display: 'none' } }}>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              {isQuote ? 'Pay & book your service' : 'Pay this invoice online'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {isQuote
                ? 'Complete your payment to confirm your booking — we’ll take care of the rest.'
                : 'Settle the amount due securely with your card.'}
            </Typography>
            {isQuote ? (
              <PublicDocumentPayForm
                token={payToken}
                documentType={document.type}
                quotationId={document.id}
                amount={document.total}
                onPaid={onPaid}
              />
            ) : (
              <SquareInvoicePayForm token={payToken} customer={document.customer} onPaid={onPaid} />
            )}
          </Box>
        )}

        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 3, textAlign: 'center' }}>
          {isQuote
            ? `This quote is valid until ${formatDate(document.validUntil)}. Prices may change after this date.`
            : 'Thank you for your business.'}
          {company.website ? ` · ${company.website}` : ''}
        </Typography>
      </Card>
    </Box>
  );
};

const Row = ({ label, value, bold, accent }) => (
  <Box display="flex" justifyContent="space-between" alignItems="center" py={0.5}>
    <Typography variant={bold ? 'subtitle1' : 'body2'} color={bold ? 'text.primary' : 'text.secondary'}
      fontWeight={bold ? 700 : 400}>{label}</Typography>
    <Typography sx={{ ...priceFont, fontWeight: bold ? 700 : 400, fontSize: bold ? '1.2rem' : '1rem',
      color: bold && accent ? accent : 'text.primary' }}>{value}</Typography>
  </Box>
);

export default DocumentView;
